import { LoadErrorBoundary } from '@shared/ui/load-error-boundary';
import { ValueSkeleton } from '@shared/ui/skeleton';
import { Checkbox } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { getViewerIdentity } from '@shared/lib/auth/viewer-identity';
import { categories, type CategoryId, useCategoryPreferences } from '../model/category-preferences';

import classes from './category-picker.module.css';

type CategoryPickerProps = {
  compact?: boolean;
  selection?: CategoryId[];
  onToggle?: (id: CategoryId) => void;
  focusCategoryId?: CategoryId | null;
};

const CategoryPickerView = ({ compact = false, selection, onToggle, focusCategoryId }: CategoryPickerProps) => {
  const preferences = useCategoryPreferences(getViewerIdentity()?.login);
  const selected = selection ?? preferences.selected;
  const toggle = onToggle ?? preferences.toggle;
  const waitingForServer =
    selection === undefined && Boolean(getViewerIdentity()?.login) && !preferences.loaded && !preferences.error;
  const optionRefs = useRef<Partial<Record<CategoryId, HTMLElement>>>({});

  useEffect(() => {
    if (!focusCategoryId) return;
    const frame = window.requestAnimationFrame(() => {
      const option = optionRefs.current[focusCategoryId];
      option?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      option?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusCategoryId]);

  return (
    <LoadErrorBoundary
      error={selection === undefined ? preferences.error : null}
      pending={preferences.loading}
      onRetry={() => preferences.load()}
    >
      <fieldset
        className={classes.fieldset}
        data-compact={compact || undefined}
        aria-label={compact ? 'Мои категории' : undefined}
      >
        {!compact && <legend>Мои категории</legend>}
        {!compact && <p>Выберите интересные вам темы. Оставьте хотя бы одну; остальные всегда доступны.</p>}
        <div className={classes.options}>
          {categories.map(({ id, label, image }) => {
            return (
              <Checkbox.Card
                key={id}
                ref={(node) => {
                  optionRefs.current[id] = node ?? undefined;
                }}
                className={classes.option}
                data-category={id}
                data-target={focusCategoryId === id || undefined}
                data-selected={(!waitingForServer && selected.includes(id)) || undefined}
                checked={selected.includes(id)}
                disabled={
                  waitingForServer ||
                  (selection === undefined && !!preferences.error) ||
                  (selection === undefined && preferences.saving) ||
                  (selected.length === 1 && selected.includes(id))
                }
                onChange={() => toggle(id)}
                aria-label={label}
              >
                <span className={classes.icon} aria-hidden="true">
                  <img src={image} alt="" />
                </span>
                <span className={classes.label}>{label}</span>
                {waitingForServer ? (
                  <ValueSkeleton width={20} label="Загружаем выбор категории" />
                ) : (
                  <Checkbox.Indicator className={classes.check} checked={selected.includes(id)} aria-hidden="true" />
                )}
              </Checkbox.Card>
            );
          })}
        </div>
      </fieldset>
    </LoadErrorBoundary>
  );
};

export const CategoryPicker = observer(CategoryPickerView);
